import type { NumberType } from '../model/form.types';

// A JS number keeps about 16 significant digits, so a 20 digit answer is stored as a canonical
// decimal string: no thousands separator, '.' as the decimal point, never '-0'. The screens show
// it in pt-BR, where '.' groups thousands and ',' separates the decimals.
export const DECIMAL_MAX_DIGITS = 20;
export const DECIMAL_MAX_PLACES = 5;

const CANONICAL_PATTERN = /^-?(0|[1-9]\d*)(\.\d+)?$/;
const PLAIN_DECIMAL_PATTERN = /^(-?)(\d+)(?:[.,](\d+))?$/;

export function isCanonicalDecimal(text: string): boolean {
  return CANONICAL_PATTERN.test(text) && !/^-0(\.0+)?$/.test(text);
}

function canonicalFrom(negative: boolean, integerDigits: string, fractionDigits: string): string {
  const integer = integerDigits.replace(/^0+(?=\d)/, '') || '0';
  const body = fractionDigits.length > 0 ? `${integer}.${fractionDigits}` : integer;
  const isZero = /^[0.]+$/.test(body);
  return negative && !isZero ? `-${body}` : body;
}

export function parsePtBrDecimal(text: string): string | null {
  const match = /^(-?)([\d.]*)(?:,(\d*))?$/.exec(text.trim());
  if (!match) return null;
  const integerDigits = match[2].replace(/\./g, '');
  const fractionDigits = match[3] ?? '';
  if (integerDigits.length === 0 && fractionDigits.length === 0) return null;
  return canonicalFrom(match[1] === '-', integerDigits, fractionDigits);
}

export function formatPtBrDecimal(canonical: string): string {
  const negative = canonical.startsWith('-');
  const [integer, fraction] = (negative ? canonical.slice(1) : canonical).split('.');
  const groups: string[] = [];
  for (let end = integer.length; end > 0; end -= 3) groups.unshift(integer.slice(Math.max(end - 3, 0), end));
  const sign = negative ? '-' : '';
  const decimals = fraction === undefined ? '' : `,${fraction}`;
  return sign + groups.join('.') + decimals;
}

// String(1e21) is '1e+21' and String(1e-7) is '1e-7'; the exponent is expanded by moving the
// point in the digit string, because toFixed and Intl would round or depend on the runtime.
function expandNumber(value: number): string {
  const text = String(value);
  const match = /^(-?)(\d)(?:\.(\d+))?e([+-]\d+)$/.exec(text);
  if (!match) return text;
  const [, sign, lead, rest = '', exponentText] = match;
  const digits = `${lead}${rest}`;
  const pointAt = 1 + Number(exponentText);
  if (pointAt <= 0) return `${sign}0.${'0'.repeat(-pointAt)}${digits}`;
  if (pointAt >= digits.length) return `${sign}${digits}${'0'.repeat(pointAt - digits.length)}`;
  return `${sign}${digits.slice(0, pointAt)}.${digits.slice(pointAt)}`;
}

// Reads what a number field may hold: a canonical string, a finite number saved before the answers
// became strings, or a "Valor" the old builder saved with a single ',' or '.' as the decimal mark.
// A pt-BR text with thousands dots is not read here: only the screens parse pt-BR.
export function decimalFromStored(value: unknown): string | null {
  let text: string;
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return null;
    text = expandNumber(value);
  } else if (typeof value === 'string') {
    text = value.trim();
  } else {
    return null;
  }
  const match = PLAIN_DECIMAL_PATTERN.exec(text);
  if (!match) return null;
  return canonicalFrom(match[1] === '-', match[2], match[3] ?? '');
}

export function countDecimalPlaces(canonical: string): number {
  const point = canonical.indexOf('.');
  return point === -1 ? 0 : canonical.length - point - 1;
}

// The zero before the point is written by the canonical form, not typed, so it does not count.
export function countDigits(canonical: string): number {
  return canonical.replace(/^-?0\./, '').replace(/\D/g, '').length;
}

export function compareDecimals(a: string, b: string): -1 | 0 | 1 {
  const places = Math.max(countDecimalPlaces(a), countDecimalPlaces(b));
  const scaled = (canonical: string) => {
    const [integer, fraction = ''] = canonical.split('.');
    return BigInt(`${integer}${fraction.padEnd(places, '0')}`);
  };
  const left = scaled(a);
  const right = scaled(b);
  if (left === right) return 0;
  return left < right ? -1 : 1;
}

// The decimal places only count once the author chose "Decimal" and gave a valid count; a matrix
// without a type keeps accepting any number of places.
export function effectiveDecimalPlaces(numberType?: NumberType, decimalPlaces?: number): number | undefined {
  if (numberType !== 'decimal' || decimalPlaces === undefined) return undefined;
  return Number.isInteger(decimalPlaces) && decimalPlaces >= 0 && decimalPlaces <= DECIMAL_MAX_PLACES
    ? decimalPlaces
    : undefined;
}

export interface DecimalTypingOptions {
  numberType?: NumberType;
  decimalPlaces?: number;
}

// A paste such as "1,234.56" uses the en-US marks: when the last '.' comes after the last ',', the
// marks are swapped so the text reads as pt-BR instead of becoming 1,23456.
export function normalizePastedDecimal(text: string): string {
  const lastDot = text.lastIndexOf('.');
  const lastComma = text.lastIndexOf(',');
  if (lastDot === -1 || lastComma === -1 || lastDot < lastComma) return text;
  return text.replace(/,/g, '').replace(/\.(?=[^.]*$)/, ',');
}

// What the pt-BR number inputs keep while the person types: digits, a leading '-', thousands dots
// and one ','. "Número inteiro" drops the ',' and what follows, "Decimal" stops at the chosen
// places, and the digits stop at DECIMAL_MAX_DIGITS.
export function sanitizeDecimalTyping(text: string, options: DecimalTypingOptions = {}): string {
  const negative = text.trimStart().startsWith('-');
  const kept = text.replace(/[^\d.,]/g, '');
  const commaAt = kept.indexOf(',');
  let integerPart = commaAt === -1 ? kept : kept.slice(0, commaAt);
  let fractionPart = commaAt === -1 ? null : kept.slice(commaAt + 1).replace(/[.,]/g, '');

  if (options.numberType === 'integer') fractionPart = null;
  const places = effectiveDecimalPlaces(options.numberType, options.decimalPlaces);
  if (fractionPart !== null && places !== undefined) {
    fractionPart = places === 0 ? null : fractionPart.slice(0, places);
  }

  let budget = DECIMAL_MAX_DIGITS;
  integerPart = integerPart.replace(/\d/g, (digit) => (budget-- > 0 ? digit : ''));
  if (fractionPart !== null) fractionPart = fractionPart.slice(0, Math.max(budget, 0));

  const sign = negative ? '-' : '';
  const decimals = fractionPart === null ? '' : `,${fractionPart}`;
  return sign + integerPart + decimals;
}

export function numberRangeMessage(minValue?: string, maxValue?: string): string {
  if (minValue !== undefined && maxValue !== undefined) {
    return `O valor deve estar entre ${formatPtBrDecimal(minValue)} e ${formatPtBrDecimal(maxValue)}.`;
  }
  if (minValue !== undefined) return `O valor deve ser no mínimo ${formatPtBrDecimal(minValue)}.`;
  return `O valor deve ser no máximo ${formatPtBrDecimal(maxValue ?? '')}.`;
}

export const INTEGER_MESSAGE = 'O valor deve ser um número inteiro.';
export const MAX_DIGITS_MESSAGE = `O valor deve ter no máximo ${DECIMAL_MAX_DIGITS} dígitos.`;

export function decimalPlacesMessage(places: number): string {
  return places === 1 ? 'O valor deve ter no máximo 1 casa decimal.' : `O valor deve ter no máximo ${places} casas decimais.`;
}
